/**
 * 3D Rekonstrüksiyon — the workstation screen.
 *
 * The mockup shows a solved point cloud with twenty-four camera frusta, a
 * pipeline reading "Hazır" through five stages, and Mesh/Doku "Bekliyor". Two
 * of those are claims this system cannot make.
 *
 * **Nothing waits for dense.** The available COLMAP build has no CUDA, so
 * `patch_match_stereo` refuses to run - there is no queue that Mesh and Doku
 * are sitting in. Rendering them as "Bekliyor" would tell the user to come
 * back later for something that is never coming. They read
 * "Kullanılamıyor", and the panel says why.
 *
 * **There is no viewer yet.** The API serves artifact metadata, not artifact
 * bytes, so a point cloud cannot be fetched and drawn. Rather than show the
 * mockup's picture and let it look like the user's own data, the viewport
 * states what exists and offers what can actually be done with it. A picture
 * of somebody else's reconstruction presented as yours is the exact failure
 * this project keeps writing rules about.
 *
 * Every number comes from `metrics`, and a metric the backend did not send is
 * a dash.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createReconstruction,
  getArtifactContent,
  getLatestReconstruction,
  getReconstructionCapability,
  getReconstructionReadiness,
  listReconstructions,
} from "@/api/client";
import { ApiError } from "@/api/errors";
import type { ReconstructionJob, ReconstructionStatus, UnpreparedVideo } from "@/api/types";
import { parseCameraPoses, cameraFrustums, type SolvedCamera } from "./cameraPoses";
import { framingOf } from "./viewerFraming";
import { PointCloudViewer } from "./PointCloudViewer";
import { PreflightPanel } from "./PreflightPanel";
import { RunFailure } from "./RunFailure";
import { RunProgress } from "./RunProgress";
import { RunResult } from "./RunResult";
import { SurfaceOptions, SurfaceUnavailableNote } from "./SurfaceOptions";
import {
  DEFAULT_SURFACE_OPTION,
  SURFACE_OPTIONS,
  isSurfaceProfile,
  surfaceAvailability,
} from "./reconstructionState";
import { usePointCloud, type PointCloudState } from "./usePointCloud";
import {
  useVideoPreparation,
  type PreparationProgress,
  type VideoPreparationState,
} from "./useVideoPreparation";
import { ErrorState } from "@/components/ErrorState";
import { IconGrid } from "@/components/icons";
import {
  InspectorPanel,
  Metric,
  PageHeader,
  Section,
  type Tone,
} from "@/components/product";
import { Button, EmptyState, Loading, StatusBadge } from "@/components/ui";
import {
  RECONSTRUCTION_TERMS,
  formatCount,
  formatDecimal,
  type StatusTone,
} from "@/i18n/terms";
import { formatDateTime } from "@/lib/format";
import { useAction, useAsync } from "@/lib/useAsync";
import { useProjectContext } from "@/features/projects/ProjectScreen";
import { paths } from "@/app/paths";
import "./reconstruction.css";

/**
 * `StatusBadge` has no "accent" tone, so a running job reads as neutral there
 * rather than borrowing a colour that already means something else.
 */
function badgeTone(tone: Tone): StatusTone {
  return tone === "accent" ? "neutral" : tone;
}

const STATUS_TONE: Record<ReconstructionStatus, Tone> = {
  queued: "neutral",
  running: "accent",
  succeeded: "success",
  failed: "danger",
  cancelled: "neutral",
};

/**
 * How often an unfinished job is re-read.
 *
 * A measured production solve of 24 images took 173 seconds, and the backend
 * publishes no progress within a stage - only the status changes. So this is
 * the interval at which the *status* can change, not a progress animation:
 * frequent enough that "Sırada" turning into "İşleniyor" is noticed, slow
 * enough that a three-minute job costs about thirty requests.
 */
const POLL_MS = 5_000;

/** Queued and running are the states that still move on their own. */
function isActive(job: ReconstructionJob | null): boolean {
  return job !== null && (job.status === "queued" || job.status === "running");
}

/**
 * What to tell the user when starting was refused.
 *
 * Driven by the server's `code` where there is one, because the two 409s mean
 * opposite things: one project needs more frames and the other needs fewer,
 * and a single "çakışma" message would send half the users the wrong way.
 *
 * No count appears in any of these sentences. The floor and the ceiling belong
 * to the reconstruction profile, the API does not publish them, and a number
 * hardcoded here would keep being displayed with confidence after the profile
 * changed. The rule is stated; the arithmetic is not invented.
 */
function startFailureMessage(error: ApiError): string {
  switch (error.code) {
    case "not_enough_input":
      return (
        "Bu projede rekonstrüksiyon için yeterli kullanılabilir kare yok. " +
        "Daha fazla çekim ekleyin. Bir video, yalnızca üzerinde " +
        "rekonstrüksiyon amaçlı kare seçimi çalıştırıldıktan sonra sayılır - " +
        "yüz çözümlemesi için yapılan seçim burada kullanılmaz."
      );
    // There is deliberately no `too_many_inputs` case. Exceeding the profile's
    // budget is a supported behaviour now, not a refusal: the inputs are
    // sampled across the whole capture and the result says so. Keeping a
    // message for it would be keeping a sentence nobody can ever see.
    case "reconstruction_already_active":
      // Coded separately from `not_enough_input` because the remedy is the
      // opposite: that one says capture more, this one says wait.
      return (
        "Bu proje için bir rekonstrüksiyon şu anda sırada veya çalışıyor. " +
        "Bitmesini bekleyin; ikinci bir çalıştırma aynı girdileri ikinci kez " +
        "dondurur."
      );
    case "unknown_profile":
      return "Bu rekonstrüksiyon profili sunucuda tanımlı değil.";
    default:
      break;
  }
  switch (error.kind) {
    case "not_found":
      return "Proje bulunamadı.";
    case "conflict":
      return "Bu proje şu anda rekonstrüksiyona uygun durumda değil.";
    case "network":
      return "Sunucuya ulaşılamadı. Bağlantınızı kontrol edip tekrar deneyin.";
    case "timeout":
      return "Sunucu zamanında yanıt vermedi. İşlem başlamış olabilir; sayfayı yenileyin.";
    case "unavailable":
    case "server":
      return "Sunucu şu anda bu isteği karşılayamadı. Daha sonra tekrar deneyin.";
    default:
      return "Rekonstrüksiyon başlatılamadı.";
  }
}

/**
 * What a job is doing, for the person waiting on it.
 *
 * One function, because the badge in the header and the line in the viewport
 * were drifting: one said "İşleniyor" for twenty minutes while the other said
 * something else, and both were derived separately from the same job.
 *
 * `queued` splits in two. A job waiting behind other work is a queue; a job
 * waiting because nothing that can run it is connected is a different fact,
 * and a customer can act on the difference - or at least stop wondering
 * whether the product is broken.
 */
function jobStateLabel(
  job: ReconstructionJob,
  { surfaceAvailable }: { surfaceAvailable: boolean },
): string {
  if (job.status === "queued") {
    // Only a surface job can be waiting for a GPU. A sparse job queues behind
    // the CPU worker, which is always there.
    const needsSurface = isSurfaceProfile(job.profile_name);
    return needsSurface && !surfaceAvailable
      ? RECONSTRUCTION_TERMS.queuedNoWorker
      : RECONSTRUCTION_TERMS.queuedBehindWork;
  }
  if (job.status === "running") {
    return job.current_phase
      ? (RECONSTRUCTION_TERMS.phase[job.current_phase] ?? RECONSTRUCTION_TERMS.phaseUnknown)
      : RECONSTRUCTION_TERMS.phaseUnknown;
  }
  // A status this build has not been taught is still a status the server sent,
  // and a blank badge is worse than a plain word.
  return RECONSTRUCTION_TERMS.status[job.status] ?? "İşleniyor";
}

/**
 * The version of the engine that actually ran the job.
 *
 * Three answers are not versions and must not be shown as one. `undetermined`
 * means nothing has executed the job yet; `unknown` means the worker looked and
 * its binary would not say; `unavailable` is what the old jobs recorded back
 * when the API stamped its own machine's answer - and that machine, by design,
 * had no engine. None of the three may be dressed up as a build number, and the
 * capability marker is only appended when there is a real version to append it
 * to.
 */
function engineVersionLabel(job: ReconstructionJob): string {
  const version = (job.engine_version ?? "").trim();
  if (!version || ["undetermined", "unknown", "unavailable"].includes(version)) {
    return "Bilinmiyor";
  }
  const capability = job.engine_capabilities;
  if (capability === "nocuda") return `${version} (CUDA yok)`;
  if (capability === "cuda") return `${version} (CUDA)`;
  return version;
}

/** Megabytes, for telling someone why a file will not be drawn. */
function megabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
}

/**
 * What the viewport says when it is not drawing a cloud.
 *
 * Each branch describes the actual situation of this job's own artifact. None
 * of them promises a viewer that is coming, and none implies geometry exists
 * when it does not.
 */
function viewportNoteFor(
  job: ReconstructionJob | null,
  active: boolean,
  cloud: PointCloudState,
): string {
  if (active) {
    // No percentage: the engine publishes none, and a bar that moves on a
    // timer is a claim about progress nobody measured.
    return "Sunucuda çalışıyor. Durum kendiliğinden güncellenir; bu işlem birkaç dakika sürebilir.";
  }
  switch (cloud.status) {
    case "loading":
      return "Nokta bulutu indiriliyor…";
    case "failed":
      return cloud.message;
    case "too-large":
      return `Bu nokta bulutu tarayıcıda gösterilemeyecek kadar büyük (${megabytes(cloud.byteSize)}). Dosya olarak indirilebilir.`;
    case "empty":
      return "Bu çalışmada gösterilecek nokta üretilmedi.";
    default:
      break;
  }
  if (job?.status === "failed") return "Bu çalışma geometri üretmedi.";
  return "Bu çalışmaya ait bir nokta bulutu bulunmuyor.";
}

/** `1:23` from a duration, or nothing when the backend did not measure one. */
function runtime(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return null;
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

const PREPARATION_LABEL: Record<VideoPreparationState, string> = {
  waiting: "Sırada",
  working: "Hazırlanıyor",
  ready: "Hazır",
  failed: "Hazırlanamadı",
};

const PREPARATION_TONE: Record<VideoPreparationState, StatusTone> = {
  waiting: "neutral",
  working: "neutral",
  ready: "success",
  failed: "danger",
};

/**
 * The step between "I uploaded videos" and "reconstruct them".
 *
 * Everything technical about this is deliberately absent. The user is told
 * that frames will be chosen from their videos and offered one button; which
 * selector runs, that a separate selection already exists for face analysis,
 * and where the frames are written are facts about the implementation.
 */
function VideoPreparationPanel({
  videos,
  progress,
  busy,
  onPrepare,
}: {
  videos: UnpreparedVideo[];
  progress: PreparationProgress;
  busy: boolean;
  onPrepare: () => void;
}) {
  return (
    <Section title="Videolar rekonstrüksiyon için hazırlanmalı">
      <p className="muted">
        Bu projedeki videolardan 3D rekonstrüksiyon için uygun kareler seçilecek.
        Bu işlem videonun uzunluğuna göre birkaç dakika sürebilir.
      </p>

      <ul className="prep">
        {videos.map((video) => {
          const state = progress.states[video.media_asset_id] ?? "waiting";
          const length = runtime(video.duration_seconds);
          return (
            <li key={video.media_asset_id} className="prep__item">
              {/* The user's own filename is the only identity shown. */}
              <span className="prep__name" title={video.original_filename}>
                {video.original_filename || "Video"}
              </span>
              {length && <span className="prep__meta">{length}</span>}
              {busy || progress.total > 0 ? (
                <StatusBadge tone={PREPARATION_TONE[state]}>
                  {PREPARATION_LABEL[state]}
                </StatusBadge>
              ) : null}
            </li>
          );
        })}
      </ul>

      <div className="row">
        <Button variant="primary" onClick={onPrepare} busy={busy} disabled={busy}>
          Videoları Hazırla
        </Button>
        {/*
          Counted, not estimated. Videos finish one at a time, so this is a
          fact; the engine reports nothing about progress *within* a video and
          a percentage would have to be invented.
        */}
        {progress.total > 0 && (
          <span className="muted">
            {progress.finished} / {progress.total} video hazırlandı
          </span>
        )}
      </div>

      {progress.failures.length > 0 && (
        <div className="stack-tight" role="alert">
          {progress.failures.map((failure) => (
            <p key={failure.name} className="muted">
              <strong>{failure.name || "Video"}</strong> — {failure.message}
            </p>
          ))}
        </div>
      )}
    </Section>
  );
}

export function ProjectReconstructionTab() {
  const { project } = useProjectContext();
  const navigate = useNavigate();

  const latest = useCallback(
    (signal: AbortSignal) => getLatestReconstruction(project.id, { signal }),
    [project.id],
  );
  const history = useCallback(
    (signal: AbortSignal) => listReconstructions(project.id, { signal }),
    [project.id],
  );
  const readinessOf = useCallback(
    (signal: AbortSignal) => getReconstructionReadiness(project.id, { signal }),
    [project.id],
  );
  const capabilityOf = useCallback(
    (signal: AbortSignal) => getReconstructionCapability({ signal }),
    [],
  );
  const { state, reload } = useAsync(latest);
  const { state: historyState, reload: reloadHistory } = useAsync(history);
  const { state: readinessState, reload: reloadReadiness } = useAsync(readinessOf);
  const { state: capabilityState } = useAsync(capabilityOf);
  const readiness = readinessState.status === "success" ? readinessState.data : null;

  /**
   * Whether this deployment can currently reach a surface at all.
   *
   * Both stages, because a mesh needs a dense cloud to be built from, and the
   * server derives the answer from what its engines report - the local probe
   * plus any worker still heartbeating. So the action appears when a machine
   * that can do the work is actually there, and goes away on its own when it
   * is not. Never assumed while the answer is still loading or failed to
   * arrive: an offered action that cannot run is worse than an absent one.
   */
  /**
   * Three answers, and the third is not a "no".
   *
   * `Boolean(dense && mesh)` used to be the whole test, so a capability
   * request that was merely slow read exactly like a GPU that does not exist -
   * and the surface controls vanished with no explanation. Loading and failed
   * are `unknown` now, which the screen states rather than acts on.
   */
  const capability = capabilityState.status === "success" ? capabilityState.data : null;
  const availability = surfaceAvailability(capability, {
    loading: capabilityState.status === "loading" || capabilityState.status === "idle",
  });
  const surfaceAvailable = availability === "available";

  // A project with no reconstruction is a 404 from this endpoint, which is an
  // empty state and not an error the user should see as one.
  const missing = state.status === "error" && state.error.status === 404;
  const job = state.status === "success" ? state.data : null;
  const metrics = job?.metrics ?? null;

  const start = useAction(
    useCallback(() => createReconstruction(project.id), [project.id]),
  );

  /**
   * The surface reconstruction, as a *second* action rather than a mode.
   *
   * A profile name, not a machine: which worker executes it is the scheduler's
   * business, and a frontend naming one would be wrong the first time the fleet
   * changed. The sparse button beside it is untouched and keeps using the
   * deployment's own default, so the ordinary path is unaffected by anything
   * here.
   */
  // Which ceiling the next surface run uses. Local to this screen and reset on
  // every visit: it describes one action about to be taken, not a preference
  // about the project.
  const [surfaceImages, setSurfaceImages] = useState<number>(
    DEFAULT_SURFACE_OPTION.images,
  );
  const surfaceProfile =
    SURFACE_OPTIONS.find((option) => option.images === surfaceImages) ??
    DEFAULT_SURFACE_OPTION;

  const startSurface = useAction(
    useCallback(
      () => createReconstruction(project.id, { profile: surfaceProfile.profile }),
      [project.id, surfaceProfile.profile],
    ),
  );

  const runStart = useCallback(async () => {
    const created = await start.run();
    // Only on success: a refused start leaves the screen as it was, with the
    // reason next to the button that caused it.
    if (created) {
      reload();
      reloadHistory();
    }
  }, [start, reload, reloadHistory]);

  const runStartSurface = useCallback(async () => {
    const created = await startSurface.run();
    if (created) {
      reload();
      reloadHistory();
    }
  }, [startSurface, reload, reloadHistory]);

  // Preparation changes what the project can do, so the server is asked again
  // rather than the screen assuming it worked. That is also what keeps a
  // partial failure honest: the videos that failed are still listed as
  // unprepared, because they still are.
  const afterPreparation = useCallback(() => {
    reloadReadiness();
  }, [reloadReadiness]);
  const preparation = useVideoPreparation(afterPreparation);

  // Memoised because it feeds a `useCallback`: a fresh `[]` on every render
  // would rebuild the prepare handler each time, which is the sort of churn
  // that eventually restarts something it should not.
  const unprepared = useMemo(
    () => readiness?.unprepared_videos ?? [],
    [readiness],
  );
  const needsPreparation = unprepared.length > 0;
  const prepareVideos = useCallback(() => {
    void preparation.prepare(unprepared);
  }, [preparation, unprepared]);

  // An unfinished job is re-read until it stops moving. Without this the user
  // clicks, sees "Sırada", and has to guess when to refresh - and the measured
  // solve takes minutes, so guessing is most of the experience.
  const active = isActive(job);
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => {
      reload();
      reloadHistory();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [active, reload, reloadHistory]);

  // Either button's refusal, in the one place refusals are already shown. Both
  // call the same route and fail for the same reasons, so a second message in a
  // second position would only ask the reader to work out which was which.
  const startFailure = start.error ?? startSurface.error;
  const startError = startFailure ? startFailureMessage(startFailure) : null;

  /**
   * Whether the API would accept a run right now.
   *
   * Read from the endpoint, never re-derived here: the backend computes it as
   * "no blocking preflight findings", and a second derivation on this side is
   * how a button starts disagreeing with the endpoint behind it. Missing
   * readiness means the question has not been answered yet, and an unanswered
   * question must not disable a working action.
   */
  const blocked = readiness ? !readiness.can_start : false;

  // Only the sparse cloud is drawable. Camera poses are `colmap-txt` and the
  // log is text; neither is geometry, and neither is offered as though it were.
  const pointCloudArtifact =
    job?.artifacts.find(
      (artifact) =>
        artifact.artifact_kind === "sparse_point_cloud" && artifact.artifact_format === "ply",
    ) ?? null;
  const { state: cloudState, retry: retryCloud } = usePointCloud(
    job?.id ?? null,
    pointCloudArtifact,
  );

  const posesArtifact = job?.artifacts.find(a => a.artifact_kind === "camera_poses");
  const [cameras, setCameras] = useState<SolvedCamera[]>([]);
  const [cameraError, setCameraError] = useState(false);
  const cameraJobId = job?.id;
  const cameraArtifactId = posesArtifact?.id;
  const cameraBytes = posesArtifact?.byte_size;
  useEffect(() => {
    setCameras([]);
    setCameraError(false);
    if (!cameraJobId || !cameraArtifactId) return;
    if ((cameraBytes ?? 0) > 48 * 1024 * 1024) {
      setCameraError(true);
      return;
    }
    const controller = new AbortController();
    void getArtifactContent(cameraJobId, cameraArtifactId, { signal: controller.signal })
      .then(bytes => {
        if (!controller.signal.aborted) setCameras(parseCameraPoses(new TextDecoder().decode(bytes)));
      })
      .catch(() => { if (!controller.signal.aborted) setCameraError(true); });
    return () => controller.abort();
  }, [cameraJobId, cameraArtifactId, cameraBytes]);
  const cameraLines = useMemo(() => cloudState.status === "ready" && cameras.length > 0
    ? cameraFrustums(cameras, framingOf(cloudState.cloud.positions).radius * .04)
    : undefined, [cloudState, cameras]);

  const viewportNote = viewportNoteFor(job, active, cloudState);
  /** Where "Sonucu incele" scrolls to: the cloud already on this screen. */
  const viewport = useRef<HTMLElement>(null);

  if (state.status === "loading" && state.data === null) return <Loading />;
  if (state.status === "error" && !missing) {
    return <ErrorState error={state.error} />;
  }

  return (
    <div className="stack">
      <PageHeader
        title="3D Rekonstrüksiyon"
        subtitle={project.name}
        actions={
          job ? (
            <StatusBadge tone={badgeTone(STATUS_TONE[job.status])}>
              {jobStateLabel(job, { surfaceAvailable })}
            </StatusBadge>
          ) : null
        }
      />

      {/*
        One place to start a run, whether or not this project has one already.
        It used to be two: a header row when a job existed and an empty-state
        block when it did not, each with its own copy of the controls and its
        own idea of what they were called.
      */}
      {!active && (
        <StartPanel
          hasPreviousRun={job !== null}
          availability={availability}
          images={surfaceImages}
          onImages={setSurfaceImages}
          blocked={blocked}
          preparing={preparation.busy}
          quickBusy={start.busy}
          surfaceBusy={startSurface.busy}
          onQuick={runStart}
          onSurface={runStartSurface}
          error={startError}
          onSessions={() => navigate(paths.projectSessions(project.id))}
        />
      )}

      {/* The run itself: one story, from status and the reported phase. */}
      {job && active && <RunProgress job={job} surfaceAvailable={surfaceAvailable} />}

      {job && job.status === "failed" && (
        <RunFailure
          job={job}
          projectId={project.id}
          onRerun={runStart}
          rerunning={start.busy}
          rerunDisabled={blocked || preparation.busy}
        />
      )}

      {job && job.status === "succeeded" && (
        <RunResult
          job={job}
          projectId={project.id}
          onInspect={
            cloudState.status === "ready"
              ? () => viewport.current?.scrollIntoView({ behavior: "smooth", block: "center" })
              : null
          }
        />
      )}

      {/*
        Shown whether or not a job exists. Inputs change after a solve - a
        video added, a file deleted - and the preflight for the *next* run is
        what this answers.
      */}
      {readiness && !active && (
        <PreflightPanel readiness={readiness} projectId={project.id} />
      )}

      {needsPreparation && !active && (
        <VideoPreparationPanel
          videos={unprepared}
          progress={preparation.progress}
          busy={preparation.busy}
          onPrepare={prepareVideos}
        />
      )}

      {!job ? (
        <EmptyState
          title="Henüz 3D rekonstrüksiyon oluşturulmadı."
          description="3D sonuç, bu projedeki çekimlerden üretilir. Başlattığınızda sunucuda sıraya alınır; sayfayı açık tutmanız gerekmez."
        />
      ) : (
        <div className="workspace">
          <div className="stack">
            {/*
              What is drawn is this job's own cloud, fetched from its own
              artifact. There is no stand-in: every state below is about *this*
              file, and none of them is a picture of somebody else's data.
            */}
            <section className="viewport" ref={viewport}>
              {cloudState.status === "ready" ? (
                <>
                  <PointCloudViewer cloud={cloudState.cloud} cameraLines={cameraLines} />
                  {cameras.length > 0 && <p className="faint">{cameras.length} kamera konumu ve bakış yönü · Kamera işaretleri şematiktir.</p>}
                  {cameraError && <p role="status">Kamera yönleri yüklenemedi.</p>}
                </>
              ) : (
                <div className="viewport__inner">
                  <IconGrid size={40} />
                  <p className="viewport__state">{jobStateLabel(job, { surfaceAvailable })}</p>
                  <p className="viewport__note">{viewportNote}</p>
                  {cloudState.status === "failed" && (
                    <Button onClick={retryCloud}>Yeniden Dene</Button>
                  )}
                </div>
              )}
            </section>
          </div>

          <div className="stack">
            <InspectorPanel title={`Çalışma v${job.version}`}>
              <Metric
                label="Durum"
                value={RECONSTRUCTION_TERMS.status[job.status]}
                tone={STATUS_TONE[job.status]}
              />
              <Metric label="Kullanılan fotoğraf" value={formatCount(job.input_image_count, "fotoğraf")} />
              {job.sampling?.sampled && (
                <Metric
                  label="Girdi seçimi"
                  value={`${job.sampling.resolved_input_count} kullanılabilir girdiden ${job.sampling.selected_input_count}'i seçildi`}
                />
              )}
              <Metric
                label="Süre"
                value={
                  metrics?.total_seconds != null
                    ? `${Math.round(metrics.total_seconds)} sn`
                    : null
                }
              />
            </InspectorPanel>

            {/*
              The measurements, folded away.

              "Ortalama reprojection hatası", the engine build string and the
              camera registration ratio were in the customer's main hierarchy.
              None of them is a decision a customer makes, and one of them -
              a "profil bütçesi" - read as a price. They are still here,
              unchanged and exact, one disclosure away.
            */}
            <details className="run-technical">
              <summary>Teknik ayrıntılar</summary>
              <InspectorPanel title="Ölçümler">
                {/*
                  Two numbers where there used to be one, because one of them
                  was answering the wrong question.

                  "63 / 100" in a caution colour reads as thirty-seven failed
                  photographs, and on the capture that produced those figures
                  every photograph solved - the mapper built three models and
                  the product delivered the largest. The remedy for a failed
                  photograph is to retake it, which would have changed nothing
                  there. So the count that says whether the photographs were
                  any good is shown beside the count that says how much of the
                  capture reached the model, and the split is named rather
                  than left to be inferred from the gap between them.
                */}
                <Metric
                  label="Modeldeki kameralar"
                  value={
                    metrics
                      ? `${metrics.registered_camera_count} / ${metrics.input_image_count}`
                      : null
                  }
                />
                {metrics?.solved_camera_count != null &&
                metrics.solved_camera_count > metrics.registered_camera_count ? (
                  <Metric
                    label="Çözülen kameralar"
                    value={`${metrics.solved_camera_count} / ${metrics.input_image_count}`}
                    tone="success"
                  />
                ) : null}
                {metrics?.model_count != null && metrics.model_count > 1 ? (
                  <Metric
                    label="Çekim parçası"
                    value={`${metrics.model_count} ayrı grup`}
                    tone="caution"
                  />
                ) : null}
                <Metric
                  label="Kamera kayıt oranı"
                  value={metrics ? `%${(metrics.registration_ratio * 100).toFixed(1)}` : null}
                  tone={metrics && metrics.registration_ratio >= 0.9 ? "success" : "caution"}
                />
                <Metric
                  label="Seyrek noktalar"
                  value={metrics ? metrics.sparse_point_count.toLocaleString("tr-TR") : null}
                />
                <Metric
                  label="Ortalama reprojection hatası"
                  value={formatDecimal(metrics?.mean_reprojection_error, " px")}
                />
                <Metric label="Motor" value={job.engine_name.toUpperCase()} />
                <Metric label="Motor sürümü" value={engineVersionLabel(job)} />
              </InspectorPanel>
            </details>

            {historyState.status === "success" && historyState.data.length > 0 && (
              <InspectorPanel title="Geçmiş">
                {/*
                  A re-run adds a version; it never replaces one. Said here,
                  beside the list that proves it.
                */}
                <p className="faint">{RECONSTRUCTION_TERMS.rerunNote}</p>
                <ul className="history">
                  {historyState.data.map((entry) => (
                    <li key={entry.id} className="history__item">
                      <span className={`history__dot history__dot--${STATUS_TONE[entry.status]}`} />
                      <span className="history__version">v{entry.version}</span>
                      <span className="history__state">
                        {RECONSTRUCTION_TERMS.status[entry.status]}
                      </span>
                      <span className="history__when">{formatDateTime(entry.created_at)}</span>
                    </li>
                  ))}
                </ul>
              </InspectorPanel>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The two things a customer can start, named by what they get.
 *
 * "Rekonstrüksiyonu Başlat" and "Yüzey Modeli Oluştur" sat side by side with
 * nothing saying that the first produces no model and the second takes far
 * longer. Each now carries the sentence that makes the choice, and the surface
 * option keeps its controls on screen when no machine can run it - with the
 * reason - rather than disappearing and leaving the reader to wonder where
 * their buttons went.
 */
function StartPanel({
  hasPreviousRun,
  availability,
  images,
  onImages,
  blocked,
  preparing,
  quickBusy,
  surfaceBusy,
  onQuick,
  onSurface,
  error,
  onSessions,
}: {
  hasPreviousRun: boolean;
  availability: ReturnType<typeof surfaceAvailability>;
  images: number;
  onImages: (images: number) => void;
  blocked: boolean;
  preparing: boolean;
  quickBusy: boolean;
  surfaceBusy: boolean;
  onQuick: () => void;
  onSurface: () => void;
  error: string | null;
  onSessions: () => void;
}) {
  const busy = quickBusy || surfaceBusy || preparing;
  return (
    <Section title={hasPreviousRun ? "Yeni bir çalışma başlat" : "3D sonucu oluştur"}>
      <div className="startpanel">
        <div className="startpanel__option">
          <h3 className="startpanel__title">{RECONSTRUCTION_TERMS.quickCheckAction}</h3>
          <p className="muted">{RECONSTRUCTION_TERMS.quickCheckNote}</p>
          {/*
            Disabled, not hidden, while a blocker stands: the preflight above
            says why, and a control that vanishes leaves nothing to explain.
          */}
          <Button variant="primary" onClick={onQuick} busy={quickBusy} disabled={busy || blocked}>
            {RECONSTRUCTION_TERMS.quickCheckAction}
          </Button>
        </div>

        <div className="startpanel__option">
          <h3 className="startpanel__title">{RECONSTRUCTION_TERMS.surfaceAction}</h3>
          <p className="muted">{RECONSTRUCTION_TERMS.surfaceNote}</p>
          <SurfaceUnavailableNote availability={availability} />
          <SurfaceOptions
            value={images}
            availability={availability}
            disabled={busy}
            onChange={onImages}
          />
          <Button
            onClick={onSurface}
            busy={surfaceBusy}
            disabled={busy || blocked || availability !== "available"}
          >
            {RECONSTRUCTION_TERMS.surfaceAction}
          </Button>
        </div>
      </div>

      {/*
        Beside the buttons that caused it, not as a page-level error: the
        screen is fine, one action was refused.
      */}
      {error && (
        <p className="muted" role="alert">
          {error}
        </p>
      )}

      {!hasPreviousRun && (
        <div className="row">
          <Button onClick={onSessions}>Çekimlere Git</Button>
        </div>
      )}
    </Section>
  );
}
